"use client";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Trash2, ExternalLink, LogOut, FileText } from "lucide-react";
import SEOHead from "../SEOHead";
import { Button } from "../ui/button";
import api, { clearBlogAdminToken } from "../../lib/api";

export default function BlogAdminDashboard() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.blogAdmin.listPosts();
      setPosts(Array.isArray(data) ? data : []);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setDeletingId(post._id);
    try {
      await api.blogAdmin.deletePost(post._id);
      setPosts((prev) => prev.filter((p) => p._id !== post._id));
    } catch (err) {
      alert(err.message || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const logout = async () => {
    try {
      await api.blogAdmin.logout();
    } catch {
      /* ignore */
    }
    clearBlogAdminToken();
    navigate("/blog-admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <SEOHead title="Blog Admin" noindex nofollow disableCanonical />

      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#252F4F]">Blog posts</h1>
            <p className="text-sm text-gray-500">Manage your uploaded blog articles.</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate("/blog-admin/new")} className="bg-[#252F4F] hover:bg-[#1e2640]">
              <Plus className="h-4 w-4 mr-2" /> New post
            </Button>
            <Button variant="outline" onClick={logout}>
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-lg bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#252F4F]" />
          </div>
        ) : posts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-12 text-center">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No posts yet.</p>
            <Button onClick={() => navigate("/blog-admin/new")} className="bg-[#252F4F] hover:bg-[#1e2640]">
              <Plus className="h-4 w-4 mr-2" /> Create your first post
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post._id}
                className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4"
              >
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt=""
                    className="h-14 w-20 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="h-14 w-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-gray-300" />
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#252F4F] truncate">{post.title}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        post.published
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {post.published ? "Published" : "Draft"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">/{post.slug}</p>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  {post.published && (
                    <a
                      href={`/blog/${post.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-2 text-gray-400 hover:text-[#252F4F]"
                      title="View"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={() => navigate(`/blog-admin/edit/${post._id}`, { state: { post } })}
                    className="p-2 text-gray-400 hover:text-[#252F4F]"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(post)}
                    disabled={deletingId === post._id}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    {deletingId === post._id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
